import { useEffect, useRef, useMemo } from 'react';

import type { ClarityMicroEventName } from '@/utils/clarityEvents';

/**
 * Tracks an onboarding step for the Clarity funnel (PRD §5.2 Onboarding micro
 * events). Fires `onboarding_step_started` on screen mount and exposes
 * stable callbacks for the matching `onboarding_step_completed`,
 * `onboarding_step_skipped`, and `onboarding_back_pressed` micro events.
 *
 * Drop the hook into the top of every `app/onboarding-*.tsx` screen body —
 * one line gives you the mount tracker; destructure the returned helpers
 * to tag forward / skip / back transitions:
 *
 *     const { logCompleted, logSkipped, logBack } = useOnboardingStepTracking('name');
 *     // later, inside handleContinue success branch:
 *     logCompleted();
 *
 * `duration_ms` is auto-tracked from the hook's mount time so the dashboard
 * can see how long users spend on each step.
 *
 * The dynamic import keeps the analytics import off the screen's hot path
 * and ensures a Clarity SDK failure can never break the onboarding flow.
 *
 * Re-mounts (e.g. user navigates back) re-fire the event by design — Clarity
 * dedupes per session and the dashboard cares about reached-the-step counts.
 *
 * See `documentation/01. Platform/SYS_CLARITY_TAGGING.md` for the canonical
 * site rule and the dynamic-import pattern.
 */
export type OnboardingStep =
  | 'name'
  | 'gender'
  | 'support_recipient'
  | 'goal'
  | 'addiction_type'
  | 'frequency'
  | 'interests'
  | 'sponsorship'
  | 'notifications';

// Compile-time guard: keep the hook's step union and the typed helper's union
// in sync. If the helper drifts, this assignment fails to type-check.
const _eventName: ClarityMicroEventName = 'onboarding_step_started';
void _eventName;

export interface OnboardingStepTracker {
  /** Call from the forward (handleContinue success) branch. */
  logCompleted: () => void;
  /** Call from the explicit skip handler (only on screens with a skip CTA). */
  logSkipped: () => void;
  /** Call from an explicit back-button handler. Header back arrows that
   *  short-circuit through the navigator are NOT tagged — only screens
   *  with a custom back handler should call this. */
  logBack: () => void;
}

export function useOnboardingStepTracking(step: OnboardingStep): OnboardingStepTracker {
  // Mount timestamp lives in a ref so the helpers below get a stable
  // duration_ms regardless of re-renders. Reset on remount (the entire
  // component tree is destroyed on navigator pop).
  const mountedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    mountedAtRef.current = Date.now();
    import('@/utils/clarityEvents')
      .then(({ logOnboardingStepStarted }) => logOnboardingStepStarted({ step }))
      .catch(() => {
        // Non-fatal: Clarity helpers already log warnings on real failures.
      });
  }, [step]);

  // Memoise the helper bag so call sites can put the destructured callbacks
  // in dependency arrays without retriggering effects on every render.
  return useMemo<OnboardingStepTracker>(() => ({
    logCompleted: () => {
      const duration_ms = Date.now() - mountedAtRef.current;
      import('@/utils/clarityEvents')
        .then(({ logOnboardingStepCompleted }) =>
          logOnboardingStepCompleted({ step, duration_ms })
        )
        .catch(() => {});
    },
    logSkipped: () => {
      import('@/utils/clarityEvents')
        .then(({ logOnboardingStepSkipped }) => logOnboardingStepSkipped({ step }))
        .catch(() => {});
    },
    logBack: () => {
      import('@/utils/clarityEvents')
        .then(({ logOnboardingBackPressed }) => logOnboardingBackPressed({ step }))
        .catch(() => {});
    },
  }), [step]);
}
